import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Field, Input, Label, Button } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import SpinnerIcon from '../shared/SpinnerIcon';
import GuestLayout from './GuestLayout';
import { useTheme } from '../../context/ThemeContext';
import clsx from 'clsx';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const baseUrl = process.env.REACT_APP_API_URL;

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refetchConfigs } = useTheme();
  const defaultErrorMsg = t('auth.errorOccurred');
  const [step, setStep] = useState<'login' | 'verify'>('login');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const token = localStorage.getItem('authToken');

  // check if user is already logged in
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`${baseUrl}/auth/validate-token`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const user = response.data;

        setIsValid(!!user); // if there is any valid user
      } catch (error) {
        localStorage.removeItem('authToken');
        setIsValid(false);
      }
    };

    token ? validateToken() : setIsValid(false);

    if (isValid) {
      navigate('/dashboard');
    }
  }, [navigate, token, isValid]);

  const handleLogin = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      setErrorMsg(null);
      setIsLoading(true);

      try {
        console.log('login');
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const response = await axios.post(`${baseUrl}/auth/login`, {
          email: values.email,
        });

        setIsLoading(false);
        setStep('verify');
      } catch (error) {
        setIsLoading(false);

        if (axios.isAxiosError(error) && error.response) {
          if (error.response.status === 401) {
            setErrorMsg(error.response.data.message);
          } else {
            setErrorMsg(defaultErrorMsg);
          }
        } else {
          setErrorMsg(defaultErrorMsg);
        }
      }
    },
  });

  const handleVerify = useFormik({
    initialValues: {
      otp: '',
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      setErrorMsg(null);
      setIsLoading(true);

      try {
        const response = await axios.post(`${baseUrl}/auth/verify`, {
          email: handleLogin.values.email,
          otp: values.otp,
        });

        // save token to local storage
        localStorage.setItem('authToken', response.data.access_token);

        // Re-fetch all domain configs now that we're authenticated
        refetchConfigs();

        setIsLoading(false);
        navigate('/dashboard');
      } catch (error) {
        setIsLoading(false);

        if (axios.isAxiosError(error) && error.response) {
          if (error.response.status === 401) {
            setErrorMsg(error.response.data.message);
          } else {
            setErrorMsg(defaultErrorMsg);
          }
        } else {
          setErrorMsg(defaultErrorMsg);
        }
      }
    },
  });

  const inputClass = clsx(
    'block w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-sm',
    'placeholder:text-slate-400',
    'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600',
    'transition duration-150 ease-in-out',
    'disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed'
  );

  const buttonClass = clsx(
    'w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700',
    'py-3 px-4 text-base font-semibold text-white shadow-sm',
    'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
    'transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
  );

  const errorAlert = errorMsg && (
    <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      {errorMsg}
    </div>
  );

  return (
    <GuestLayout>
      {step === 'login' ? (
        <>
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-slate-900">{t('auth.signIn')}</h1>
            <p className="mt-1 text-sm text-slate-500">{t('auth.loginSubtitle')}</p>
          </div>

          {errorAlert}

          <form onSubmit={handleLogin.handleSubmit} noValidate className="space-y-5">
            <Field>
              <Label className="block text-sm font-medium text-slate-700 mb-1.5">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={handleLogin.values.email.toLocaleLowerCase()}
                onChange={handleLogin.handleChange}
                onBlur={handleLogin.handleBlur}
                autoComplete="username"
                autoFocus
                required
                placeholder={t('auth.emailPlaceholder')}
                className={inputClass}
              />
            </Field>

            <Button type="submit" disabled={isLoading} className={buttonClass}>
              {isLoading && <SpinnerIcon />}
              {isLoading ? t('auth.sending') : t('auth.sendOtpCode')}
            </Button>
          </form>
        </>
      ) : (
        <>
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-slate-900">{t('auth.verifyOtp')}</h1>
            <p className="mt-1 text-sm text-slate-500">{t('auth.verifySubtitle')}</p>
          </div>

          {errorAlert}

          <form onSubmit={handleVerify.handleSubmit} noValidate className="space-y-5">
            <Field>
              <Label className="block text-sm font-medium text-slate-700 mb-1.5">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={handleLogin.values.email}
                onChange={handleLogin.handleChange}
                required
                disabled
                className={inputClass}
              />
            </Field>

            <Field>
              <Label className="block text-sm font-medium text-slate-700 mb-1.5">{t('auth.verificationCode')}</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={handleVerify.values.otp}
                onChange={handleVerify.handleChange}
                required
                placeholder={t('auth.codePlaceholder')}
                className={inputClass}
              />
            </Field>

            <Button type="submit" disabled={isLoading} className={buttonClass}>
              {isLoading && <SpinnerIcon />}
              {isLoading ? t('auth.verifying') : t('auth.verify')}
            </Button>

            <button
              type="button"
              onClick={() => {
                setStep('login');
                setErrorMsg(null);
                handleVerify.resetForm();
              }}
              className="w-full text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-150"
            >
              {t('auth.backToLogin')}
            </button>
          </form>
        </>
      )}
    </GuestLayout>
  );
};

export default Auth;
